<template>
  <div class="content">
    <div class="settings">
      <div class="tool">
        <Multiselect
          v-model="localItem.tool"
          label="Tool"
          placeholder="Select a tool"
          :searchable="true"
          :options="toolOptions"
          :disabled="!$hasPermission('prompts.write') || isBusy"
          @input="onToolChange"
        />
      </div>
      <p v-if="toolDescription" class="tool-description">{{ toolDescription }}</p>
      <div v-if="loadingSchema" class="hint">Loading parameters…</div>
      <div v-if="error" class="error">{{ error }}</div>
      <ul v-if="paramFields.length" class="params">
        <li v-for="field in paramFields" :key="field.name" :class="['param', field.kind]">
          <Checkbox
            v-if="field.kind === 'boolean'"
            v-model="localItem.args[field.name]"
            :label="fieldLabel(field)"
            :disabled="!$hasPermission('prompts.write') || isBusy"
            @input="onChange"
          />
          <Multiselect
            v-else-if="field.kind === 'enum'"
            :value="enumArgValue(field.name)"
            :label="fieldLabel(field)"
            :options="field.enumOptions"
            :searchable="false"
            :disabled="!$hasPermission('prompts.write') || isBusy"
            @input="value => onEnumInput(field, value)"
          />
          <TextField
            v-else-if="field.kind === 'number'"
            :value="numberArgDisplay(field.name)"
            :label="fieldLabel(field)"
            type="number"
            :disabled="!$hasPermission('prompts.write') || isBusy"
            @input="value => onNumberInput(field, value)"
          />
          <TextField
            v-else
            :value="stringArgValue(field.name)"
            :label="fieldLabel(field)"
            :disabled="!$hasPermission('prompts.write') || isBusy"
            :multiline="true"
            :rows="2"
            @input="value => onStringInput(field, value)"
          />
          <p v-if="field.description" class="param-hint">{{ field.description }}</p>
        </li>
      </ul>
      <div v-else-if="localItem.tool && !loadingSchema && !error" class="hint">
        This tool has no bindable parameters for a tool link.
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Multiselect from '@/components/common/Multiselect';
import TextField from '@/components/common/TextField';
import Checkbox from '@/components/common/Checkbox';

export default {
  components: {
    Multiselect,
    TextField,
    Checkbox
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    suggestions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localItem: {
        ...this.item,
        tool: this.item.tool || '',
        args: { ...(this.item.args || {}) }
      },
      loadingSchema: false,
      error: null,
      paramFields: [],
      loadedDescription: null
    };
  },
  computed: {
    ...mapState({
      tools: state => state.nlp.tools || []
    }),
    ...mapGetters({
      isBusy: 'prompts/isBusy'
    }),
    toolOptions() {
      return this.tools.filter(t => !t.clientSide).map(t => t.name);
    },
    toolDescription() {
      if (!this.localItem.tool) {
        return '';
      }

      const fromSchema = (this.loadedDescription || '').trim();
      if (fromSchema) {
        return fromSchema;
      }

      const listed = this.tools.find(t => t.name === this.localItem.tool);
      return ((listed && listed.description) || '').trim();
    }
  },
  async mounted() {
    if (this.localItem.tool) {
      await this.loadSchema(this.localItem.tool, false);
    }
  },
  methods: {
    ...mapActions({
      getTool: 'nlp/getTool'
    }),
    fieldLabel(field) {
      return field.required ? field.name + ' *' : field.name;
    },
    stringArgValue(name) {
      const value = this.localItem.args[name];
      return typeof value === 'string' ? value : '';
    },
    numberArgDisplay(name) {
      const value = this.localItem.args[name];
      return value === undefined || value === null ? '' : value;
    },
    enumArgValue(name) {
      const value = this.localItem.args[name];
      return value === undefined || value === null ? null : value;
    },
    schemaType(schema) {
      if (!schema) return null;
      if (Array.isArray(schema.enum) && schema.enum.length) return 'enum';
      return schema.type || null;
    },
    isComplexSchema(schema) {
      const type = this.schemaType(schema);
      return type === 'array' || type === 'object';
    },
    isToolLinkEligible(parameters) {
      const props = (parameters && parameters.properties) || {};
      const required = (parameters && parameters.required) || [];
      return required.every(name => !this.isComplexSchema(props[name]));
    },
    buildParamFields(parameters) {
      const props = (parameters && parameters.properties) || {};
      const required = new Set((parameters && parameters.required) || []);

      return Object.entries(props)
        .map(([name, schema]) => {
          const kind = this.schemaType(schema);
          if (!kind || kind === 'array' || kind === 'object') return null;
          if (['string', 'boolean', 'number', 'enum'].indexOf(kind) === -1) return null;

          return {
            name: name,
            kind: kind,
            required: required.has(name),
            description: ((schema && schema.description) || '').trim(),
            enumOptions: kind === 'enum' ? schema.enum.filter(v => String(v).trim()) : []
          };
        })
        .filter(Boolean);
    },
    applyParameters(parameters, resetArgs) {
      if (!this.isToolLinkEligible(parameters)) {
        this.error = 'This tool requires complex parameters and cannot be used as a tool link.';
        this.paramFields = [];
        return false;
      }

      this.paramFields = this.buildParamFields(parameters);

      if (resetArgs) {
        const args = {};
        for (const field of this.paramFields) {
          const value = this.defaultArgValue(field);
          if (value !== undefined) {
            args[field.name] = value;
          }
        }
        this.localItem.args = args;
      } else {
        const cleaned = {};
        for (const field of this.paramFields) {
          const existing = this.localItem.args[field.name];
          if (existing === undefined || existing === null || existing === '') {
            const value = this.defaultArgValue(field);
            if (value !== undefined) {
              cleaned[field.name] = value;
            }
            continue;
          }

          if (typeof existing === 'object' || String(existing) === '[object Object]') {
            const value = this.defaultArgValue(field);
            if (value !== undefined) {
              cleaned[field.name] = value;
            }
            continue;
          }

          if (field.kind === 'number') {
            const value = this.parseNumber(existing);
            if (value !== undefined) {
              cleaned[field.name] = value;
            }
            continue;
          }

          if (field.kind === 'boolean') {
            cleaned[field.name] = existing === true;
            continue;
          }

          cleaned[field.name] = String(existing);
        }
        this.localItem.args = cleaned;
      }

      this.onChange();
      return true;
    },
    defaultArgValue(field) {
      if (field.kind === 'boolean') {
        return field.required ? false : undefined;
      }

      if (field.kind === 'number') {
        return undefined;
      }

      return field.required ? '' : undefined;
    },
    parseNumber(raw) {
      if (raw === '' || raw === null || raw === undefined) {
        return undefined;
      }

      const value = typeof raw === 'number' ? raw : Number(raw);
      return Number.isFinite(value) ? value : undefined;
    },
    sanitizeArgs() {
      const args = {};

      for (const field of this.paramFields) {
        const raw = this.localItem.args[field.name];

        if (field.kind === 'boolean') {
          if (raw === true) {
            args[field.name] = true;
          } else if (field.required) {
            args[field.name] = false;
          }
          continue;
        }

        if (raw === undefined || raw === null || raw === '') {
          continue;
        }

        if (typeof raw === 'object') {
          continue;
        }

        if (field.kind === 'number') {
          const value = this.parseNumber(raw);
          if (value !== undefined) {
            args[field.name] = value;
          }
          continue;
        }

        const text = String(raw);
        if (!text || text === '[object Object]') {
          continue;
        }

        args[field.name] = text;
      }

      return args;
    },
    onEnumInput(field, value) {
      if (value === undefined || value === null || value === '') {
        this.$delete(this.localItem.args, field.name);
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        this.$set(this.localItem.args, field.name, value);
      } else {
        this.$delete(this.localItem.args, field.name);
      }
      this.onChange();
    },
    onNumberInput(field, raw) {
      const value = this.parseNumber(raw);
      if (value === undefined) {
        this.$delete(this.localItem.args, field.name);
      } else {
        this.$set(this.localItem.args, field.name, value);
      }
      this.onChange();
    },
    onStringInput(field, value) {
      const text = typeof value === 'string' ? value : value == null ? '' : String(value);
      if (!text) {
        this.$delete(this.localItem.args, field.name);
      } else {
        this.$set(this.localItem.args, field.name, text);
      }
      this.onChange();
    },
    async onToolChange(name) {
      this.localItem.tool = name || '';
      this.localItem.args = {};
      this.paramFields = [];
      this.loadedDescription = null;
      this.error = null;
      this.onChange();

      if (name) {
        await this.loadSchema(name, true);
      }
    },
    async loadSchema(name, resetArgs) {
      this.loadingSchema = true;
      this.error = null;

      try {
        // Instant UI from configurations list (now includes parameters).
        const listed = this.tools.find(t => t.name === name);
        if (listed && listed.parameters) {
          this.loadedDescription = listed.description || null;
          this.applyParameters(listed.parameters, resetArgs);
        }

        const tool = await this.getTool(name);
        if (!tool) {
          if (!this.paramFields.length) {
            this.error = 'Failed to load tool schema for ' + name;
            this.paramFields = [];
            this.loadedDescription = null;
          }
          return;
        }

        this.loadedDescription = tool.description || this.loadedDescription;
        if (!tool.parameters || !tool.parameters.properties) {
          if (!this.paramFields.length) {
            this.error = 'Tool schema has no parameters: ' + name;
          }
          return;
        }

        this.applyParameters(tool.parameters, resetArgs);
      } catch (e) {
        if (!this.paramFields.length) {
          this.error = 'Failed to load tool schema: ' + ((e && e.message) || e);
          this.paramFields = [];
          this.loadedDescription = null;
        }
      } finally {
        this.loadingSchema = false;
      }
    },
    onChange() {
      this.$emit('change', {
        ...this.localItem,
        args: this.sanitizeArgs()
      });
    }
  }
};
</script>

<style scoped lang="scss">
.content {
  display: grid;
  gap: 5px;
  padding: 10px;

  .settings {
    display: grid;
    gap: 10px;
    padding: 5px;

    .tool {
      padding: 5px;
      max-width: 360px;
    }

    .tool-description {
      margin: 0;
      padding: 0 5px;
      max-width: 720px;
      font-size: 0.8rem;
      line-height: 1.45;
      color: var(--theme-on-surface-variant, #666);
    }

    .error {
      color: red;
      font-size: 0.75rem;
      font-style: italic;
    }

    .hint {
      font-size: 0.75rem;
      font-style: italic;
      padding: 0 5px;
    }

    .params {
      display: flex;
      flex-wrap: wrap;
      list-style: none;
      padding: 5px;
      gap: 10px;
      max-height: none;

      .param {
        min-width: 180px;
        flex: 1 1 220px;

        &.string {
          flex: 1 1 100%;
          min-width: 100%;
        }

        .param-hint {
          margin: 4px 0 0;
          font-size: 0.72rem;
          line-height: 1.35;
          color: var(--theme-on-surface-variant, #666);
        }
      }
    }
  }
}
</style>
